import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import {
  orderFilesSearchFragment,
  placeFilesSearchFragment,
} from "@/features/office/_components/file-library/file-library-search-fragment";
import { OrderDetail } from "@/features/office/orders/detail";
import { listItemsSearchFragment } from "@/features/office/places/detail/main/list-items/list-items-search-fragment";
import { smartActionShadowSearchFragment } from "@/features/office/smart-actions/shadow";
import { maskSearchFragment } from "@/features/shared/route-masking";

const querySchema = z.object({
  "main-tab": z.string().optional(),
  "link-relation": z.enum(["RELATED", "PARENT", "DUPLICATED"]).optional(),
  "chat-tab": z.string().optional(),
  "thread-id": z.string().optional(),
  "message-id": z.string().optional(),
  "comment-id": z.string().optional(),
  "aside-tab": z.string().optional(),
  "place-detail-id": z.string().optional(),
  "visit-id": z.string().optional(),
  // The rest of the visit-report deep link, which the thread's `VISIT_REPORT` card
  // writes. Undeclared keys are stripped by `validateSearch`, so without these three the
  // card lands on the Visits tab with no report open. Lenient strings, never enums —
  // a `SearchParamError` here takes the whole page down (webapp-routing.md).
  "visit-tab": z.string().optional(),
  "visit-detail-tab": z.string().optional(),
  "report-id": z.string().optional(),
  "task-id": z.string().optional(),
  "line-item-id": z.string().optional(),
  // Quotes tab (behind `rollout_quote_feature`): the open quote, the option
  // tab the builder shows (an option id the quote no longer has falls back
  // silently), `preview` when the customer's copy is overlaid, and the
  // appearance / expiry the Issue dialog is holding while it hands the preview
  // its unsaved choice. All lenient strings — a strict enum here throws
  // `SearchParamError` and takes the whole page down (webapp-routing.md).
  "quote-id": z.string().optional(),
  "quote-option": z.string().optional(),
  "quote-view": z.string().optional(),
  "preview-appearance": z.string().optional(),
  "preview-expires": z.string().optional(),
  // Birdie share links land here (`?aside-tab=birdie&conversation-id=`;
  // `session-id` is the retired Convex engine's form) — without these the
  // schema strips the id and the link opens a fresh chat.
  "session-id": z.string().optional(),
  "conversation-id": z.string().optional(),
  // The Files tabs' search, filters and display options — the order's own tab
  // and the aside's place tab each under their own keys.
  ...orderFilesSearchFragment,
  ...placeFilesSearchFragment,
  // The aside's place Lists tab: its filters (Type and Proposed by) and
  // done-or-archived toggle.
  ...listItemsSearchFragment,
  // The Smart Action popup over this order (`?smartActionId=`, SA-D33): the
  // order card and the composer hint open it here, and closing it keeps the order.
  ...smartActionShadowSearchFragment,
  ...maskSearchFragment,
});

export const routeId = "/office/orders/$orderId";

export const Route = createFileRoute("/office/orders/$orderId")({
  component: OrderDetail,
  validateSearch: querySchema,
});
